import { Button, TableCell } from '@material-ui/core';
import React, { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Combobox from '../../../../components/atom/Combobox';
import {
  Control,
  DeepMap,
  FieldArrayWithId,
  FieldError,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useWatch,
} from 'react-hook-form';
import { CouponPackMasterFormInput } from '../../../../types/couponPackMaster';
import { SmartCheckImageUrl, SmartCheckSetting, useCouponMastersLazyQuery } from '../../../../graphql/generated';
import { useSnackbar } from 'notistack';
import { CouponMasterListConditionFormValues } from '../../../coupon-master/list/List';
import { OrganizationContext } from '../../../../components/contexts/organization';
import { NumberFieldController } from '../../../../components/organisms/form/controllers/NumberFieldController';

type TableCellInputNumberOfUsesProps = {
  control: Control<CouponPackMasterFormInput>;
  watch: UseFormWatch<CouponPackMasterFormInput>;
  trigger: UseFormTrigger<CouponPackMasterFormInput>;
  index: number;
  fieldId: string;
  errors: DeepMap<CouponPackMasterFormInput, FieldError>;
  setValue: UseFormSetValue<CouponPackMasterFormInput>;
  fieldsLength: number;
  onRemove: (index: number) => void;
  previousValues: CouponPackMasterFormInput;
  isDisabled: boolean;
  setSmartCheckSettings?: React.Dispatch<
    React.SetStateAction<
      Record<
        string,
        {
          smartCheckImageUrl?: SmartCheckImageUrl;
          smartCheckSetting?: SmartCheckSetting;
        }
      >
    >
  >;
};

type CouponMasterOption = {
  value: string;
  label: string;
  couponMasterName: string;
  couponMasterCode: string;
  imageUrl?: string;
  validityEndDateTime?: string;
  validityStartDateTime?: string;
  displayStartDate?: string;
  displayEndDate?: string;
  displayValidityDate?: boolean;
  telNumber?: string;
  information?: string;
  couponAmount?: number;
  smartCheckImageUrl?: SmartCheckImageUrl;
  smartCheckSetting?: SmartCheckSetting;
  imageServer?: string;
  couponNameHtml?: string;
};

const PageItemsCount = 25;

export const TableCellInputNumberOfUses: React.FC<TableCellInputNumberOfUsesProps> = ({
  control,
  watch,
  index,
  fieldId,
  errors,
  setValue,
  fieldsLength,
  onRemove,
  previousValues,
  trigger,
  isDisabled,
  setSmartCheckSettings,
}) => {
  const [couponMasterList, setCouponMasterList] = useState<CouponMasterOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const isChangeInput = useRef(false);
  const isRendered = useRef(false);
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext.organization;
  const couponMasterName = useWatch({
    control,
    name: `products.${index}.couponMasterName`,
  });
  const allProducts = useWatch({
    control,
    name: 'products',
  });
  const [listQuery, queryContext] = useCouponMastersLazyQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setLoading(false);
      setIsLoadingMore(false);
      if (data?.couponMasters?.pageInfo?.nextToken) {
        setNextToken(data.couponMasters?.pageInfo?.nextToken);
        setHasMore(true);
      } else {
        setNextToken(null);
        setHasMore(false);
      }
    },
    onError: () => {
      setLoading(false);
      setIsLoadingMore(false);
      enqueueSnackbar('サーバーエラーが発生した', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    },
  });

  const onSearchCouponMasters = useCallback(
    (data: CouponMasterListConditionFormValues) => {
      if (organization) {
        setLoading(true);
        isChangeInput.current = true;
        setNextToken(null);
        setCouponMasterList([]);
        listQuery({
          variables: {
            organizationId: organization.id,
            [data.searchCondition]: data.value,
            limit: PageItemsCount,
            smartCheckFlag: true,
          },
        });
      }
    },
    [listQuery, organization]
  );

  const loadMoreCouponMasters = useCallback(() => {
    if (!hasMore || isLoadingMore || !organization || !nextToken) return;

    setIsLoadingMore(true);
    listQuery({
      variables: {
        organizationId: organization.id,
        limit: PageItemsCount,
        nextToken: nextToken,
        smartCheckFlag: true,
      },
    });
  }, [hasMore, isLoadingMore, organization, nextToken, listQuery]);

  useEffect(() => {
    const matchingPreviousProduct = previousValues?.products?.find((product) => product.id === fieldId);
    if (organization && !isRendered.current && !matchingPreviousProduct?.id) {
      setLoading(true);
      listQuery({
        variables: {
          organizationId: organization.id,
          limit: PageItemsCount,
          smartCheckFlag: true,
        },
      });
    }
  }, [listQuery, organization, previousValues, fieldId]);

  useEffect(() => {
    // Find matching product by fieldId instead of using index directly
    // This prevents data from being incorrectly applied after remove/append operations
    const matchingPreviousProduct = previousValues?.products?.find((product) => product.id === fieldId);

    if (matchingPreviousProduct?.id && !isRendered.current && !isChangeInput.current) {
      setValue(`products.${index}.id`, matchingPreviousProduct.id);
      setValue(`products.${index}.couponMasterName`, matchingPreviousProduct.couponMasterName || '');
      setValue(`products.${index}.couponMasterCode`, matchingPreviousProduct.couponMasterCode || '');
      setValue(`products.${index}.imageUrl`, (matchingPreviousProduct as any)?.imageUrl || '');
      setValue(`products.${index}.validityEndDateTime`, matchingPreviousProduct.validityEndDateTime || '');
      setValue(`products.${index}.validityStartDateTime`, matchingPreviousProduct.validityStartDateTime || '');
      setValue(`products.${index}.displayEndDate`, matchingPreviousProduct.displayEndDate || '');
      setValue(`products.${index}.displayStartDate`, matchingPreviousProduct.displayStartDate || '');
      setValue(`products.${index}.displayValidityDate`, matchingPreviousProduct.displayValidityDate || false);
      setValue(`products.${index}.telNumber`, matchingPreviousProduct.telNumber || '');
      setValue(`products.${index}.information`, matchingPreviousProduct.information || '');
      setValue(`products.${index}.couponAmount`, matchingPreviousProduct.couponAmount || 0);
      setValue(`products.${index}.imageServer`, matchingPreviousProduct.imageServer || '');

      // Set option default for combobox
      setCouponMasterList([
        {
          value: matchingPreviousProduct.id,
          label: matchingPreviousProduct.couponMasterCode || '',
          couponMasterName: matchingPreviousProduct.couponMasterName || '',
          couponMasterCode: matchingPreviousProduct.couponMasterCode || '',
          imageUrl: matchingPreviousProduct.imageUrl || '',
          validityEndDateTime: matchingPreviousProduct.validityEndDateTime,
          validityStartDateTime: matchingPreviousProduct.validityStartDateTime,
          displayStartDate: matchingPreviousProduct.displayStartDate,
          displayEndDate: matchingPreviousProduct.displayEndDate,
          displayValidityDate: matchingPreviousProduct.displayValidityDate,
          telNumber: matchingPreviousProduct.telNumber,
          information: matchingPreviousProduct.information,
          couponAmount: matchingPreviousProduct.couponAmount,
          smartCheckImageUrl: matchingPreviousProduct?.smartCheckImageUrl,
          smartCheckSetting: matchingPreviousProduct?.smartCheckSetting,
          imageServer: matchingPreviousProduct?.imageServer,
          couponNameHtml: matchingPreviousProduct?.couponNameHtml,
        },
      ]);

      isRendered.current = true;
    }
  }, [previousValues, index, fieldId, setValue]);

  useEffect(() => {
    if (!queryContext.data?.couponMasters) return;
    const convertData = queryContext.data.couponMasters.nodes.map((couponMaster) => ({
      value: couponMaster.id,
      label: couponMaster.couponCode,
      couponMasterName: couponMaster.couponName,
      couponMasterCode: couponMaster.couponCode,
      imageUrl: couponMaster.productImageUrl ?? undefined,
      validityEndDateTime: couponMaster.validityEndDateTime,
      validityStartDateTime: couponMaster.validityStartDateTime || '',
      displayStartDate: couponMaster.displayStartDate,
      displayEndDate: couponMaster.displayEndDate,
      displayValidityDate: couponMaster.displayValidityDate ?? undefined,
      telNumber: couponMaster.telNumber ?? undefined,
      information: couponMaster.information ?? undefined,
      couponAmount: couponMaster.couponAmount ?? undefined,
      smartCheckImageUrl: couponMaster.smartCheckImageUrl ?? undefined,
      smartCheckSetting: couponMaster.smartCheckSetting ?? undefined,
      imageServer: couponMaster.barcode?.imageServer ?? undefined,
      couponNameHtml: couponMaster.couponNameHtml ?? undefined,
    }));

    const selectedIds = allProducts?.map((product, idx) => (idx !== index ? product?.id : null)).filter(Boolean);
    const filteredData = convertData.filter((option) => !selectedIds?.includes(option.value));

    if (isLoadingMore) {
      const existingIds = new Set(couponMasterList.map((item) => item.value));
      const newItems = filteredData.filter((item) => !existingIds.has(item.value));
      setCouponMasterList([...couponMasterList, ...newItems]);
    } else {
      setCouponMasterList(filteredData);
    }
  }, [queryContext.data?.couponMasters, allProducts, index]);

  useEffect(() => {
    if (!watch(`products.${index}.quantity` as any)) return;

    trigger(`products.${index}.maxCouponsPerOrder`);
    trigger(`products.${index}.quantity`);
  }, [
    watch(`products.${index}.quantity` as any),
    watch(`products.${index}.maxCouponsPerOrder` as any),
    index,
    trigger,
  ]);

  return (
    <>
      <TableCell align="left" scope="row">
        <Combobox
          control={control}
          name={`products[${index}].id`}
          error={errors.products?.[index]?.id}
          size="medium"
          options={couponMasterList}
          rule={{
            required: 'クーポンコードは必須です',
          }}
          maxLength={30}
          loading={loading}
          onOptionSelect={(item) => {
            setValue(`products.${index}.id`, String(item.value ?? ''));
            setValue(`products.${index}.couponMasterName`, String(item.couponMasterName ?? ''));
            setValue(`products.${index}.couponMasterCode`, String(item.couponMasterCode ?? ''));
            setValue(`products.${index}.imageUrl`, String(item.imageUrl ?? ''));
            setValue(`products.${index}.validityEndDateTime`, String(item.validityEndDateTime ?? ''));
            setValue(`products.${index}.validityStartDateTime`, String(item.validityStartDateTime ?? ''));
            setValue(`products.${index}.displayStartDate`, String(item.displayStartDate ?? ''));
            setValue(`products.${index}.displayEndDate`, String(item.displayEndDate ?? ''));
            setValue(
              `products.${index}.displayValidityDate`,
              item.displayValidityDate ? Boolean(item.displayValidityDate) : false
            );
            setValue(`products.${index}.telNumber`, String(item.telNumber ?? ''));
            setValue(`products.${index}.information`, String(item.information ?? ''));
            setValue(`products.${index}.couponAmount`, Number(item.couponAmount ?? ''));
            setValue(`products.${index}.imageServer`, String(item.imageServer ?? ''));
            setValue(`products.${index}.couponNameHtml`, String(item.couponNameHtml ?? ''));

            if (setSmartCheckSettings && (item.smartCheckImageUrl || item.smartCheckSetting) && item.value) {
              setSmartCheckSettings((prev) => ({
                ...prev,
                [`${item.value}`]: {
                  smartCheckImageUrl: item.smartCheckImageUrl as SmartCheckImageUrl | undefined,
                  smartCheckSetting: item.smartCheckSetting as SmartCheckSetting | undefined,
                },
              }));
            }
          }}
          onInputChange={(value) => {
            onSearchCouponMasters({ searchCondition: 'couponCode', value });
          }}
          disabled={isDisabled}
          onScrollBottom={loadMoreCouponMasters}
        />
      </TableCell>
      <TableCell align="left" scope="row">
        {couponMasterName}
      </TableCell>
      <TableCell align="left" scope="row">
        <NumberFieldController
          required
          name={`products[${index}].quantity`}
          size="small"
          isFormatWithComma
          rule={{
            required: '利用回数は必須です',
            min: {
              value: 1,
              message: '利用回数は0より大きい整数を入力してください',
            },
            max: {
              value: 999,
              message: '発行枚数は999枚を超えて設定できません',
            },
          }}
          disabled={isDisabled}
        />
      </TableCell>
      <TableCell align="left" scope="row">
        <NumberFieldController
          name={`products[${index}].maxCouponsPerOrder`}
          size="small"
          isFormatWithComma
          rule={{
            min: {
              value: 1,
              message: '1回の利用上限数は1以上の整数を入力してください',
            },
            max: {
              value:
                Number(watch(`products.${index}.quantity` as any)) < 90
                  ? Number(watch(`products.${index}.quantity` as any))
                  : 90,
              message:
                Number(watch(`products.${index}.quantity` as any)) < 90
                  ? '1回の利用上限数は利用回数を超えて設定できません'
                  : '1回の利用上限数は90枚を超えて設定できません',
            },
          }}
          disabled={isDisabled}
        />
      </TableCell>
      <TableCell align="center" scope="row">
        <Button
          type="button"
          size="small"
          color="primary"
          disabled={fieldsLength <= 1 || isDisabled}
          onClick={() => onRemove(index)}
        >
          削除
        </Button>
      </TableCell>
    </>
  );
};
